import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowLeft, CheckCircle, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BETENBOUGH_LOGO = "/images/betenbough-logo-new.png";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (response.ok) {
        setIsSuccess(true);
        toast.success("Password reset successfully!");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to reset password");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#f7f8f3] flex flex-col">
        <header className="bg-[#357c2a]">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Link to="/refer">
              <img src={BETENBOUGH_LOGO} alt="Betenbough Homes" className="h-10 w-auto" />
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <Card className="w-full max-w-md shadow-lg border-[#357c2a]/10">
            <CardHeader className="text-center">
              <CardTitle className="text-xl text-red-600">Invalid Link</CardTitle>
              <CardDescription>
                This password reset link is invalid or has expired.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/forgot-password">
                <Button className="w-full bg-[#84c77f] text-[#357c2a] hover:bg-[#d4db7e]">
                  Request New Link
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8f3] flex flex-col">
      <header className="bg-[#357c2a]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/refer">
            <img src={BETENBOUGH_LOGO} alt="Betenbough Homes" className="h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Card className="shadow-lg border-[#357c2a]/10">
            <CardHeader className="text-center pb-2">
              {isSuccess ? (
                <>
                  <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-[#84c77f] flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-[#357c2a]" />
                  </div>
                  <CardTitle className="text-2xl text-[#357c2a]">Password Reset!</CardTitle>
                  <CardDescription>
                    Your password has been successfully reset. You can now log in with your new password.
                  </CardDescription>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-[#357c2a] flex items-center justify-center">
                    <Lock className="h-8 w-8 text-[#84c77f]" />
                  </div>
                  <CardTitle className="text-2xl text-[#357c2a]">Set New Password</CardTitle>
                  <CardDescription>
                    Enter your new password below.
                  </CardDescription>
                </>
              )}
            </CardHeader>
            <CardContent>
              {isSuccess ? (
                <Button
                  onClick={() => navigate("/login")}
                  className="w-full bg-[#84c77f] text-[#357c2a] hover:bg-[#d4db7e] font-semibold"
                >
                  Go to Login
                </Button>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="password" className="text-[#357c2a]">New Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#357c2a]/40" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter new password"
                        className="pl-10 pr-10 border-[#357c2a]/20 focus:border-[#84c77f] focus:ring-[#84c77f]"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        minLength={8}
                        required
                        data-testid="reset-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#357c2a]/40 hover:text-[#357c2a]"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Must be at least 8 characters</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="confirmPassword" className="text-[#357c2a]">Confirm Password</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#357c2a]/40" />
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm new password"
                        className="pl-10 pr-10 border-[#357c2a]/20 focus:border-[#84c77f] focus:ring-[#84c77f]"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        data-testid="reset-confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#357c2a]/40 hover:text-[#357c2a]"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full bg-[#84c77f] text-[#357c2a] hover:bg-[#d4db7e] font-semibold"
                    disabled={isSubmitting}
                    data-testid="reset-submit-btn"
                  >
                    {isSubmitting ? "Resetting..." : "Reset Password"}
                  </Button>
                  
                  <Link to="/login" className="block">
                    <Button variant="ghost" className="w-full text-[#357c2a]">
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
